import { transition, verticalFadeIn } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const MotionWrapper = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-in-view'] ? 'visible' : 'hidden',
    transition: {
        staggerChildren: 0.2,
    },
})) `
  display: contents;
`;
const Container = styled(SectionWrapper) `
  overflow: hidden;

  ${SectionWrapperStyles.Inner} {
    position: relative;
  }
`;
const Inner = styled.div `
  ${baseGrid}

  [data-layout='with-image'] & {
    align-items: center;
  }
`;
const FullWidthLayout = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-column: 1 / -1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 2 / span 10;
    align-items: center;
  }
`;
const QuoteWrapper = styled.div `
  color: ${brand.primary.white};
  margin-bottom: ${fluid(24, 53)};

  [data-variant='white'] & {
    color: ${brand.primary.orange};
  }

  svg {
    ${transition('stroke-dashoffset', 1.2, 'ease')};

    width: ${fluid(67, 127)};
    height: ${fluid(50, 96)};
    stroke-dasharray: 260;
    stroke-dashoffset: 260;
    transition-delay: 0.65s;

    [data-visible='true'] & {
      stroke-dashoffset: 0;
    }
  }
`;
const LogoWrapper = styled(m.div).attrs(() => ({ variants: verticalFadeIn(10) })) `
  max-width: ${fluid(67, 138)};
  margin-bottom: ${fluid(17, 53)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
  }
`;
const Quote = styled(m.p).attrs(() => ({ variants: verticalFadeIn(10) })) `
  ${fonts.poppins.semiBoldItalic};

  font-size: ${fluid(17, 38)};
  line-height: ${fluid(26, 50)};
  color: ${brand.primary.white};
  white-space: pre-line;
  margin-bottom: ${fluid(8, 53)};

  [data-variant='white'] & {
    color: ${brand.primary.black};
  }

  [data-variant='orange'] & {
    --titleHighlight: ${brand.tertiary.sun};
  }

  span {
    background: var(--titleHighlight, currentColor);
    background-clip: text;
    color: transparent;
    display: inline;
  }

  [data-layout='with-image'] & {
    font-size: ${fluid(17, 28)};
    line-height: ${fluid(26, 42)};
    text-align: left;
    margin-bottom: 4px;
  }

  @media ${from(Device.TabletLarge)} {
    text-align: center;
  }
`;
const QuoteeWrapper = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    },
})) `
  display: flex;
  align-items: center;
  gap: ${fluid(10, 20)};
  margin-bottom: ${fluid(0, 60)};

  [data-layout='with-image'] & {
    gap: 14px;
    margin-bottom: 0;
    margin-top: ${fluid(20, 40)};
  }

  @media ${until(Device.TabletLarge)} {
    order: 1;
  }
`;
const QuoteeCircle = styled(m.div).attrs(() => ({ variants: verticalFadeIn(10) })) `
  --size: ${fluid(56, 108)};

  position: relative;
  flex-shrink: 0;
  border-radius: 50%;
  border: ${fluid(2, 3)} solid ${brand.primary.white};
  width: var(--size);
  height: var(--size);

  [data-layout='with-image'] & {
    --size: ${fluid(45, 77)};
  }

  [data-variant='white'] & {
    border-color: ${brand.primary.black};
  }

  img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 85%;
    height: 85%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    overflow: hidden;
    object-fit: cover;
    object-position: center center;
  }
`;
const QuoteeDetails = styled.div `
  display: flex;
  flex-direction: column;
`;
const QuoteeName = styled(m.p).attrs(() => ({ variants: verticalFadeIn(10) })) `
  ${fonts.caveat};

  font-weight: 700;
  font-size: ${fluid(24, 47)};
  line-height: ${fluid(33, 64)};
  color: ${brand.primary.white};

  [data-variant='white'] & {
    color: ${brand.primary.black};
  }

  [data-layout='with-image'] & {
    font-size: ${fluid(24, 33)};
    line-height: ${fluid(33, 46)};
  }
`;
const QuoteePosition = styled(m.p).attrs(() => ({ variants: verticalFadeIn(10) })) `
  ${fonts.poppins.regular};

  font-size: ${fluid(12, 23)};
  line-height: ${fluid(15, 28)};
  color: ${brand.primary.white};

  [data-variant='white'] & {
    color: ${brand.primary.black};
  }

  [data-layout='with-image'] & {
    font-size: ${fluid(12, 17)};
    line-height: ${fluid(14, 20)};
  }
`;
const CTAWrapper = styled(m.div).attrs(() => ({ variants: verticalFadeIn(10) })) `
  [data-layout='full-width'] & {
    display: flex;
    align-items: center;

    @media ${until(Device.TabletLarge)} {
      margin-bottom: 20px;

      ${ButtonStyles.Container} {
        --bgColourHover: transparent;

        padding: 0;
      }
    }

    @media ${from(Device.TabletLarge)} {
      justify-content: center;
    }

    @media ${from(Device.TabletLarge)} {
      [data-variant='charcoal'] &,
      [data-variant='orange'] & {
        ${ButtonStyles.Container} {
          --colourText: ${brand.tertiary.coffee};
        }
      }
    }
  }
`;
const DesktopCTA = styled.div `
  display: contents;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileCTA = styled.div `
  display: contents;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const ImageContainer = styled(m.div).attrs(() => ({ variants: verticalFadeIn(10) })) `
  z-index: 2;
  grid-column: 1 / span 10;

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 25px;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 5;
  }

  img {
    border-radius: ${fluid(8, 16)};
    overflow: hidden;
    aspect-ratio: 284 / 225;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center center;

    @media ${from(Device.TabletLarge)} {
      aspect-ratio: 636 / 418;
    }
  }
`;
const ContentWrapper = styled.div `
  grid-column: 1 / -1;
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: 1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 7 / -1;
  }

  ${ButtonStyles.Container} {
    width: max-content;
    max-width: 100%;
    padding: 0;
    --bgColourHover: transparent;
  }
`;
const FloatingQuoteWrapper = styled.div `
  position: absolute;
  z-index: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: calc(100% - 24px);
  right: -48px;

  @media ${from(Device.TabletLarge)} {
    right: unset;
    height: calc(100% + var(--sectionSpacingTop));
    width: 693px;
    left: -220px;
    top: calc((var(--sectionSpacingTop) / 2) * -1);
  }

  ${() => QuoteWrapper} {
    margin-bottom: 0;
  }

  svg {
    width: ${fluid(167, 693)};
    height: ${fluid(126, 523)};
  }
`;
export const QuoteStyles = {
    Container,
    ContentWrapper,
    CTAWrapper,
    DesktopCTA,
    FloatingQuoteWrapper,
    FullWidthLayout,
    ImageContainer,
    Inner,
    LogoWrapper,
    MobileCTA,
    MotionWrapper,
    Quote,
    QuoteeCircle,
    QuoteeDetails,
    QuoteeName,
    QuoteePosition,
    QuoteeWrapper,
    QuoteWrapper,
};
